import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useCurrencyExpirationLeft } from '@root/entities/premium/hooks'
import { DATE_FORMAT } from '@root/shared'
import { Edit1Icon, Separator as Separator_ } from '@root/shared/ui-kit'

import { premiumFontCss } from '../../constants'
import { PremiumListItem } from '../common/premium-list-item'
import { PremiumListItemButton } from '../common/premium-list-item-button'

import { ActivePremiumListItemRenew } from './active-premium-list-item-renew'

import type { CurrencyModel } from '@root/entities/currency'
import type { CurrencyWithPremium } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium/atoms'

type Props = {
  currency: CurrencyModel
  premium: PremiumMetaModel
  onEdit: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
  onExtend: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
  onClick?: (currency: CurrencyModel) => void
}

export const ActivePremiumListItem: React.FC<Props> = ({
  currency,
  premium: premiumMeta,
  onEdit,
  onExtend,
  onClick,
}) => {
  const { t } = useTranslation()
  const { formatExpirationLeft } = useCurrencyExpirationLeft()

  const [expiresAt] = useAtom(premiumMeta.expiresAt)
  const [premium] = useAtom(premiumMeta.premium)

  const handleEdit = () => onEdit({ currency, premium: premiumMeta })

  return (
    <PremiumListItem
      currency={currency}
      premiumMeta={premiumMeta}
      onClick={onClick}
      premiumInfo={
        <PremiumInfo>
          <TimeLeft>
            <Trans
              i18nKey="premium.purchased.date-due"
              components={[<span key={0} />]}
              values={{
                date: DateF.format(expiresAt, DATE_FORMAT.dd_MM_yy),
              }}
            />
          </TimeLeft>
          <Separator vertical />
          <DaysLeft>
            {premium ? formatExpirationLeft(currency, expiresAt) : undefined}
          </DaysLeft>
        </PremiumInfo>
      }
      actions={
        <>
          <ActivePremiumListItemRenew
            currency={currency}
            premium={premiumMeta}
            onExtend={onExtend}
          />
          <ButtonEdit
            radius="rounded"
            onClick={handleEdit}
            iconLeft={<Edit1Icon />}
          >
            {t('currency.edit_token')}
          </ButtonEdit>
        </>
      }
    />
  )
}

const TimeLeft = styled.div`
  text-align: left;
  font-style: normal;

  font-size: 12px;
  font-weight: 600;
  line-height: 12px;

  span {
    ${premiumFontCss}
  }
`

const DaysLeft = styled.span`
  color: var(--textA-9);

  display: grid;
  align-items: center;
  font-style: normal;
  text-align: end;

  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const ButtonEdit = styled(PremiumListItemButton)`
  background: var(--backgroundA-2);
  color: var(--text-11);
`

const PremiumInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 0.7fr;
  align-items: center;
  justify-items: center;

  gap: 16px;
`

const Separator = styled(Separator_)`
  background: var(--backgroundA-3);
  height: 32px;
`
